import { useEffect, useState } from "react";
import { fileBlobUrl, humanBytes, resolveMime } from "@/lib/files";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileWarning } from "lucide-react";
import PdfCanvasViewer from "@/components/PdfCanvasViewer";
import { toast } from "sonner";

/** In-app viewer for PDFs, images, video and audio. Download is the fallback. */
export default function FilePreviewDialog({ file, open, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !file) return;
    let revoked = null;
    setLoading(true);
    fileBlobUrl(file.id, resolveMime(file))
      .then((u) => { revoked = u; setUrl(u); })
      .catch(() => toast.error("Could not open this file"))
      .finally(() => setLoading(false));
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
      setUrl(null);
    };
  }, [open, file]);

  const type = resolveMime(file || {});
  const download = () => {
    if (!url || !file) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = file.original_filename;
    a.click();
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{file?.original_filename}</DialogTitle>
          <DialogDescription className="text-xs">In-app preview — download if you need a local copy.</DialogDescription>
        </DialogHeader>
        <div className="w-full flex items-center justify-center bg-muted/30 rounded-lg overflow-hidden min-h-[320px]">
          {loading || !url ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-20">
              <Loader2 className="w-4 h-4 animate-spin" /> Opening…
            </div>
          ) : type === "application/pdf" ? (
            <div className="w-full" data-testid="pdf-viewer">
              <PdfCanvasViewer src={url} className="h-[72vh]" testId="pdf-canvas-viewer"
                               filename={file.original_filename} />
            </div>
          ) : type.startsWith("image/") ? (
            <img src={url} alt={file.original_filename} className="max-w-full max-h-[76vh] object-contain" data-testid="image-viewer" />
          ) : type.startsWith("video/") ? (
            <video src={url} controls className="max-w-full max-h-[76vh]" data-testid="video-viewer" />
          ) : type.startsWith("audio/") ? (
            <audio src={url} controls className="w-full m-8" data-testid="audio-viewer" />
          ) : type.startsWith("text/") || type === "application/json" ? (
            <iframe title={file.original_filename} src={url} className="w-full h-[70vh] bg-background" data-testid="text-viewer" />
          ) : (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="preview-unavailable">
              <FileWarning className="w-8 h-8 mx-auto mb-3 opacity-50" />
              No in-app preview for this format ({type || "unknown"}). Download it to open locally.
            </div>
          )}
        </div>
        <DialogFooter className="items-center">
          {file?.size ? <span className="text-xs text-muted-foreground mr-auto">{humanBytes(file.size)}</span> : null}
          <Button variant="outline" onClick={download} data-testid="preview-download"><Download className="w-4 h-4 mr-2" /> Download</Button>
          <Button variant="outline" onClick={onClose} data-testid="preview-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
